/**
 * Установка CRM на экран телефона (план 110, запрос владельца).
 *
 * Установленное приложение — это не косметика: на iOS push-уведомления
 * приходят ТОЛЬКО в приложение, добавленное на экран «Домой» (Safari 16.4+),
 * во вкладке их нет вовсе. Плюс запуск без адресной строки возвращает экрану
 * те самые 100 пикселей, которых на телефоне всегда не хватает.
 *
 * ДВА РАЗНЫХ ПУТИ. Chrome/Android присылает событие `beforeinstallprompt`, и
 * установку можно предложить родным диалогом. Safari такого события не имеет —
 * там установка только руками через «Поделиться → На экран „Домой“», и
 * единственное, что мы можем, — показать инструкцию.
 */

/** Событие Chrome, которого нет в стандартных типах DOM. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Перехваченное событие установки. Хранится в модуле, а не в состоянии
 * компонента: Chrome шлёт его ОДИН раз и рано — задолго до того, как человек
 * откроет профиль. Не перехватив, предложить установку потом уже нельзя.
 */
let deferredPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // preventDefault убирает автоматическую плашку браузера: предлагаем
    // установку сами, в понятном месте и в подходящий момент.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
  });
}

/** Приложение уже запущено с ярлыка (не во вкладке браузера). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // display-mode: standalone — стандарт; navigator.standalone — Safari iOS.
  const byMedia =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const byIos = (window.navigator as { standalone?: boolean }).standalone === true;
  return byMedia || byIos;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPad с iPadOS 13+ представляется как Macintosh — отличаем по тач-точкам.
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/** Готов ли браузер показать родной диалог установки (Chrome/Android). */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/**
 * Показать родной диалог установки. Возвращает, согласился ли человек.
 * Событие одноразовое: после показа браузер новое не пришлёт.
 */
export async function promptInstall(): Promise<boolean> {
  const e = deferredPrompt;
  if (!e) return false;
  deferredPrompt = null;
  await e.prompt();
  const choice = await e.userChoice;
  return choice.outcome === 'accepted';
}
